import { toast } from "sonner";

export const PROJECT_LOCKED_CODE = "P0001";

/**
 * Feilteksten fra en Supabase-feil, uansett hvilken form den kom i (VIB-2801).
 *
 * Grunnen til at dette trengs: `PostgrestError` er IKKE en `Error`. Den er et
 * vanlig objekt med `message`, `details`, `hint` og `code`. En `catch` som
 * spør `e instanceof Error ? e.message : «noe gikk galt»` havner derfor i
 * fallbacken nøyaktig når basen hadde noe å si, og brukeren får en setning som
 * ikke peker noe sted. (`StorageError` fra storage-klienten ER en `Error`, så
 * den har aldri hatt problemet — nettopp derfor var dette vanskelig å se.)
 *
 * `details` tas med når den finnes, for Postgres legger ofte det som faktisk
 * forklarer avvisningen der, mens `message` bare sier at en policy eller en
 * constraint sa nei.
 */
export function dbFeilmelding(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === "object") {
    const o = err as { message?: unknown; details?: unknown };
    const melding = typeof o.message === "string" ? o.message.trim() : "";
    const detaljer = typeof o.details === "string" ? o.details.trim() : "";
    const tekst = [melding, detaljer].filter(Boolean).join(" — ");
    if (tekst) return tekst;
  }
  return fallback;
}

/**
 * Returns true hvis feilen er fra DB-låsen på et lukket prosjekt. Brukes i
 * mutation-hooks for å vise en distinkt toast i stedet for generisk feil.
 */
export function isProjectLockedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const code = (err as { code?: string }).code;
  return code === PROJECT_LOCKED_CODE;
}

/**
 * Standard toast for DB-låsen. Vises både ved direkte mutation-feil og når
 * en optimistisk update må rulles tilbake. Navigeringsknapp tar brukeren
 * direkte til prosjektet så de kan trykke "Åpne på nytt".
 */
export function toastProjectLocked(opts?: {
  projectId?: string | null;
  navigate?: (path: string) => void;
}) {
  const action =
    opts?.projectId && opts.navigate
      ? {
          label: "Gå til prosjekt",
          onClick: () => opts.navigate!(`/prosjekter/${opts.projectId}`),
        }
      : undefined;
  toast.error("Prosjektet er lukket", {
    description:
      "Du kan ikke endre data på lukkete prosjekter. Åpne det på nytt for å gjøre endringer.",
    duration: 8000,
    action,
  });
}
